/**
 * Prefixes a Local Storage key with `showpass.` if it doesn't already exist.
 * @param key The key to prefix
 */
export const prefixKeyWithShowpass = (key: string): string => {
  let newKey = key;
  const showpassKey = "showpass.";
  if (!newKey.startsWith(showpassKey)) {
    newKey = showpassKey.concat(key);
  }

  return newKey;
};

/**
 * A function that quickly detects the existence of the localStorage or sessionStorage object on window.
 * Note that this does not take into account checking for quotas/limits being reached on the browser's storage,
 * For that @see https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
 * @param type {String} Either "localStorage" or "sessionStorage"
 * @returns {boolean}
 */
export const isStorageAvailable = (type: "localStorage" | "sessionStorage") => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    // test as per https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#feature-detecting_localstorage
    const storage = window[type];
    const testKey = "__storage_test__";

    storage.setItem(testKey, "test");
    storage.removeItem(testKey);

    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Stores a key-value pair
 * @param key the key to set
 * @param value the value to set
 * @param expiresAt date when value expires at
 */
export const set = (
  key: string,
  value: unknown,
  expiresAt: Date | null
): void => {
  if (!isStorageAvailable("localStorage")) {
    return;
  }

  const newKey = prefixKeyWithShowpass(key);

  localStorage.setItem(
    newKey,
    JSON.stringify({
      $value: value,
      $expiresAt: expiresAt,
    })
  );
};

/**
 * Retrieves a key-value pair
 * @param key the key to set
 * @return {*} undefined if no value found, null if entry expired, or the value set
 * @deprecated Prefer parseFromLocalStorage for typed reads without JSON shape assumptions.
 */
export const get = (key: string): unknown => {
  if (!isStorageAvailable("localStorage")) {
    return undefined;
  }

  const newKey = prefixKeyWithShowpass(key);

  const entry = JSON.parse(localStorage.getItem(newKey) || "null");
  let returnValue;
  if (entry && typeof entry === "object") {
    // Cache hit — unparseable $expiresAt yields Invalid Date; comparison is false, so we expire/remove.
    if (!entry.$expiresAt || new Date(entry.$expiresAt) >= new Date()) {
      // Entry has not expired
      returnValue = entry.$value;
    } else {
      // Entry has expired
      localStorage.removeItem(newKey);
      returnValue = null;
    }
  }
  return returnValue;
};

type Optional<T> = T | undefined;

export function parseFromLocalStorage<T>(key: string): Optional<T> {
  if (!isStorageAvailable("localStorage")) {
    return undefined;
  }

  try {
    const item = localStorage.getItem(prefixKeyWithShowpass(key));
    if (item === null) {
      return undefined;
    }
    return JSON.parse(item) as T;
  } catch {
    return undefined;
  }
}

/**
 * Removes a key-value pair
 * @param key the key to set
 */
export const remove = (key: string): void => {
  if (!isStorageAvailable("localStorage")) {
    return;
  }

  localStorage.removeItem(key);
};

/**
 * Removes all key-value pairs. Optionally takes a regular expression string and removes matching.
 * @param regex {String}
 */
export const clearAll = (): void => {
  if (!isStorageAvailable("localStorage")) {
    return;
  }

  localStorage.clear();
};
